import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import productsApi from '../../../api/productsApi';
import { getErrorMessage } from '../../../utils/errorHandler';
import CategoryFormDialog from '../../../sections/admin/products/CategoryFormDialog';
import { flattenCategoryTree } from '../../../utils/categoryTree';

export default function CategoriesPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [defaultParentId, setDefaultParentId] = useState(null);
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const { data } = await productsApi.getCategories();
      setCategories(data.data);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar la lista de categorías.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filas en orden de arbol; se ocultan las que tengan algun ancestro colapsado.
  const treeRows = useMemo(() => {
    const flat = flattenCategoryTree(categories);
    const rows = [];
    let hiddenBelowDepth = null;
    flat.forEach((row) => {
      if (hiddenBelowDepth !== null && row.depth > hiddenBelowDepth) {
        return;
      }
      hiddenBelowDepth = collapsedIds.has(row.id) ? row.depth : null;
      rows.push(row);
    });
    return rows;
  }, [categories, collapsedIds]);

  const toggleCollapsed = (categoryId) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleNew = (parentId = null) => {
    setEditingCategory(null);
    setDefaultParentId(parentId);
    setFormOpen(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setDefaultParentId(null);
    setFormOpen(true);
  };

  const handleFormSaved = () => {
    setFormOpen(false);
    fetchCategories();
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) {
      return;
    }
    setDeleting(true);
    try {
      await productsApi.deleteCategory(categoryToDelete.id);
      enqueueSnackbar('Categoría eliminada correctamente.', { variant: 'success' });
      setCategoryToDelete(null);
      fetchCategories();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar la categoría.'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Nombre',
        flex: 1.5,
        minWidth: 260,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" sx={{ alignItems: 'center', height: '100%', pl: row.depth * 3 }}>
            {row.childCount > 0 ? (
              <IconButton size="small" onClick={() => toggleCollapsed(row.id)}>
                {collapsedIds.has(row.id) ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 34 }} />
            )}
            <Typography variant="body2" sx={{ fontWeight: row.depth === 0 ? 600 : 400 }}>
              {row.name}
            </Typography>
            {row.childCount > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({row.childCount})
              </Typography>
            )}
          </Stack>
        ),
      },
      { field: 'slug', headerName: 'Slug', flex: 1, minWidth: 180, sortable: false },
      {
        field: 'path',
        headerName: 'Ruta',
        flex: 1.5,
        minWidth: 220,
        sortable: false,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 140,
        getActions: (params) => [
          <GridActionsCellItem
            key="add-child"
            icon={<SubdirectoryArrowRightIcon />}
            label="Agregar subcategoría"
            onClick={() => handleNew(params.row.id)}
          />,
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Editar"
            onClick={() => handleEdit(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => setCategoryToDelete(params.row)}
          />,
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collapsedIds]
  );

  return (
    <Stack spacing={3}>
      <Stack sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }} direction="row">
        <div>
          <Typography variant="h4">Categorías</Typography>
          <Typography variant="body2" color="text.secondary">
            Categorías y subcategorías usadas para clasificar los productos del catálogo.
          </Typography>
        </div>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleNew()}>
          Nueva categoría
        </Button>
      </Stack>

      <Card>
        <DataGrid
          autoHeight
          rows={treeRows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnSorting
        />
      </Card>

      <CategoryFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={handleFormSaved}
        category={editingCategory}
        defaultParentId={defaultParentId}
        categories={categories}
      />

      <Dialog open={Boolean(categoryToDelete)} onClose={() => setCategoryToDelete(null)}>
        <DialogTitle>Eliminar categoría</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar <strong>{categoryToDelete?.name}</strong>? No podrás hacerlo si tiene
            productos o subcategorías asociadas.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryToDelete(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
